import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function ChildFormDialog({ 
  open, 
  onOpenChange, 
  child, 
  groups, 
  onSave,
  isSaving = false 
}) {
  const [form, setForm] = useState({
    id: null,
    first_name: "",
    last_name: "",
    email1: "",
    email2: "",
    phone1: "",
    phone2: "",
    phone3: "",
    gender: "",
    birth_date: "",
    notes: "",
    remarks: "",
    mnr: "",
    membership_end_date: "",
    pause_until: "",
    group_id: "",
    swimming_since: "",
  });

  // Formular mit Kind-Daten befüllen wenn Dialog geöffnet wird
  useEffect(() => {
    if (open && child) {
      // Datumsfelder für Anzeige als TT.MM.JJJJ formatieren
      const formatDateToGerman = (dateStr) => {
        if (!dateStr) return "";
        // ISO YYYY-MM-DD → DD.MM.YYYY
        if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
          const [y, m, d] = dateStr.split("T")[0].split("-");
          return `${d}.${m}.${y}`;
        }
        // Bereits deutsches Format
        if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr;
        return dateStr;
      };

      // Geschlecht normalisieren
      const normalizeGender = (gender) => {
        if (!gender) return "";
        const g = gender.toLowerCase();
        if (g === "m" || g === "männlich" || g === "junge" || g === "male") return "m";
        if (g === "w" || g === "weiblich" || g === "mädchen" || g === "female") return "w";
        return "";
      };

      setForm({
        id: child.id || null,
        first_name: child.first_name || "",
        last_name: child.last_name || "",
        // E-Mail: Backend verwendet email/secondary_email
        email1: child.email || child.email1 || "",
        email2: child.secondary_email || child.email2 || "",
        // Telefon: Backend verwendet phone/phone2/phone3
        phone1: child.phone || child.phone1 || "",
        phone2: child.phone2 || "",
        phone3: child.phone3 || "",
        gender: normalizeGender(child.gender),
        // Datum für Anzeige als TT.MM.JJJJ formatieren
        birth_date: formatDateToGerman(child.birth_date),
        notes: child.notes || "",
        // Anmerkungen: Backend verwendet remark
        remarks: child.remark || child.remarks || "",
        mnr: child.mnr || "",
        membership_end_date: formatDateToGerman(child.membership_end_date),
        pause_until: formatDateToGerman(child.pause_until),
        group_id: child.group_id || "",
        swimming_since: (() => {
          const raw = child.swim_start || child.swimming_since || "";
          if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            const [y, m, d] = raw.split("-");
            return `${d}.${m}.${y}`;
          }
          if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
            const [y, m, d] = raw.split("T")[0].split("-");
            return `${d}.${m}.${y}`;
          }
          return raw;
        })(),
      });
    } else if (open && !child) {
      // Neues Kind - leeres Formular
      setForm({
        id: null,
        first_name: "",
        last_name: "",
        email1: "",
        email2: "",
        phone1: "",
        phone2: "",
        phone3: "",
        gender: "",
        birth_date: "",
        notes: "",
        remarks: "",
        mnr: "",
        membership_end_date: "",
        pause_until: "",
        group_id: "",
        swimming_since: "",
      });
    }
  }, [open, child]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Datumsfelder von TT.MM.JJJJ zu YYYY-MM-DD konvertieren
    const germanToISO = (dateStr) => {
      if (!dateStr) return "";
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [d, m, y] = dateStr.split(".");
        return `${y}-${m}-${d}`;
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.split("T")[0];
      return dateStr;
    };
    const submitData = {
      ...form,
      birth_date: germanToISO(form.birth_date),
      membership_end_date: germanToISO(form.membership_end_date),
      pause_until: germanToISO(form.pause_until),
    };
    onSave(submitData);
  };

  const isEditing = !!form.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="child-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900">
            {isEditing ? "Kind bearbeiten" : "Neues Kind anlegen"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Persönliche Daten */}
          <div className="rounded-lg bg-slate-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Persönliche Daten</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="first_name">Vorname *</Label>
                <Input
                  id="first_name"
                  value={form.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  required
                  data-testid="child-first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nachname *</Label>
                <Input
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  required
                  data-testid="child-last-name-input"
                />
              </div>
              <div>
                <Label htmlFor="birth_date">Geburtsdatum</Label>
                <Input
                  id="birth_date"
                  type="text"
                  placeholder="TT.MM.JJJJ"
                  value={form.birth_date}
                  onChange={(e) => handleChange("birth_date", e.target.value)}
                  data-testid="child-birth-date-input"
                />
              </div>
              <div>
                <Label htmlFor="gender">Geschlecht</Label>
                <select
                  id="gender"
                  value={form.gender}
                  onChange={(e) => handleChange("gender", e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
                  data-testid="child-gender-select"
                >
                  <option value="">– Bitte wählen –</option>
                  <option value="männlich">Männlich</option>
                  <option value="weiblich">Weiblich</option>
                  <option value="divers">Divers</option>
                </select>
              </div>
            </div>
          </div>

          {/* Kontaktdaten */}
          <div className="rounded-lg bg-slate-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Kontaktdaten</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="email1">E-Mail 1</Label>
                <Input
                  id="email1"
                  type="email"
                  value={form.email1}
                  onChange={(e) => handleChange("email1", e.target.value)}
                  data-testid="child-email1-input"
                />
              </div>
              <div>
                <Label htmlFor="email2">E-Mail 2</Label>
                <Input
                  id="email2"
                  type="email"
                  value={form.email2}
                  onChange={(e) => handleChange("email2", e.target.value)}
                  data-testid="child-email2-input"
                />
              </div>
              <div>
                <Label htmlFor="phone1">Telefon 1</Label>
                <Input
                  id="phone1"
                  type="tel"
                  value={form.phone1}
                  onChange={(e) => handleChange("phone1", e.target.value)}
                  data-testid="child-phone1-input"
                />
              </div>
              <div>
                <Label htmlFor="phone2">Telefon 2</Label>
                <Input
                  id="phone2"
                  type="tel"
                  value={form.phone2}
                  onChange={(e) => handleChange("phone2", e.target.value)}
                  data-testid="child-phone2-input"
                />
              </div>
              <div>
                <Label htmlFor="phone3">Telefon 3</Label>
                <Input
                  id="phone3"
                  type="tel"
                  value={form.phone3}
                  onChange={(e) => handleChange("phone3", e.target.value)}
                  data-testid="child-phone3-input"
                />
              </div>
            </div>
          </div>

          {/* Mitgliedschaft */}
          <div className="rounded-lg bg-slate-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Mitgliedschaft</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="group_id">Gruppe *</Label>
                <select
                  id="group_id"
                  value={form.group_id}
                  onChange={(e) => handleChange("group_id", e.target.value)}
                  required
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
                  data-testid="child-group-select"
                >
                  <option value="">– Gruppe wählen –</option>
                  {groups?.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.start_time?.slice(0, 5)})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="mnr">Mitgliedsnummer</Label>
                <Input
                  id="mnr"
                  value={form.mnr}
                  onChange={(e) => handleChange("mnr", e.target.value)}
                  data-testid="child-mnr-input"
                />
              </div>
              <div>
                <Label htmlFor="swimming_since">Bei uns seit</Label>
                <Input
                  id="swimming_since"
                  type="text"
                  placeholder="z.B. 01.11.2024 oder 11-2024"
                  value={form.swimming_since}
                  onChange={(e) => handleChange("swimming_since", e.target.value)}
                  data-testid="child-swimming-since-input"
                />
              </div>
              <div>
                <Label htmlFor="pause_until">Pause bis</Label>
                <Input
                  id="pause_until"
                  type="text"
                  placeholder="TT.MM.JJJJ"
                  value={form.pause_until}
                  onChange={(e) => handleChange("pause_until", e.target.value)}
                  data-testid="child-pause-until-input"
                />
              </div>
              <div>
                <Label htmlFor="membership_end_date">Kündigung zum</Label>
                <Input
                  id="membership_end_date"
                  type="text"
                  placeholder="TT.MM.JJJJ"
                  value={form.membership_end_date}
                  onChange={(e) => handleChange("membership_end_date", e.target.value)}
                  data-testid="child-membership-end-input"
                />
              </div>
            </div>
          </div>

          {/* Bemerkungen */}
          <div className="rounded-lg bg-slate-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Bemerkungen</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="notes">Interne Notizen</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  rows={2}
                  placeholder="Nur für Trainer sichtbar..."
                  data-testid="child-notes-input"
                />
              </div>
              <div>
                <Label htmlFor="remarks">Allgemeine Bemerkungen</Label>
                <Textarea
                  id="remarks"
                  value={form.remarks}
                  onChange={(e) => handleChange("remarks", e.target.value)}
                  rows={2}
                  placeholder="z.B. Allergien, besondere Hinweise..."
                  data-testid="child-remarks-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="bg-violet-600 text-white hover:bg-violet-500"
              disabled={isSaving}
              data-testid="child-form-submit"
            >
              {isSaving ? "Speichern..." : isEditing ? "Speichern" : "Anlegen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
